import { motion } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { IMAGES } from "@/data/brand";
import { useI18n } from "@/i18n";

const lineVariant = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.4 + i * 0.14 },
  }),
};

const fade = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.9 + i * 0.12 },
  }),
};

export const Hero = () => {
  const { t, lang } = useI18n();
  const h = t.hero;

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      <div className="absolute inset-0 z-0">
        <motion.img
          src={IMAGES.heroBg}
          alt=""
          initial={{ scale: 1.15, opacity: 0 }}
          animate={{ scale: 1, opacity: 0.5 }}
          transition={{ duration: 1.8, ease: [0.22, 1, 0.36, 1] }}
          className="w-full h-full object-cover object-right"
        />
        <div className="absolute inset-0 bg-[radial-gradient(120%_100%_at_20%_50%,#0a0f1a_35%,rgba(10,15,26,0.4)_100%)]" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0f1a]/50 via-transparent to-[#0a0f1a]" />
      </div>

      <motion.img
        src={IMAGES.orb}
        alt=""
        initial={{ opacity: 0, scale: 0.7 }}
        animate={{ opacity: 0.9, scale: 1 }}
        transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1], delay: 0.5 }}
        className="float-orb pointer-events-none absolute right-[-6%] top-1/2 -translate-y-1/2 z-[1] w-[420px] h-[420px] lg:w-[560px] lg:h-[560px] rounded-full object-cover opacity-90 hidden md:block mix-blend-lighten"
      />

      <div className="relative z-10 mx-auto max-w-[1400px] w-full px-6 lg:px-10 pt-28">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.25 }}
          className="flex items-center gap-3 mb-8"
          data-testid="hero-kicker"
        >
          <span className="h-px w-10 bg-gradient-to-r from-blue-400 to-cyan-400" />
          <span className="text-xs uppercase tracking-[0.35em] text-white/50">
            {h.kicker}
          </span>
        </motion.div>

        <h1
          key={lang}
          className="font-display font-light tracking-tight text-5xl sm:text-6xl lg:text-7xl leading-[1.02] max-w-4xl"
        >
          {h.headline.map((line, i) => (
            <span key={i} className="block overflow-hidden py-1">
              <motion.span
                custom={i}
                variants={lineVariant}
                initial="hidden"
                animate="visible"
                className="block"
              >
                <span className="text-white">{line.plain}</span>
                <span className="text-gradient font-normal">{line.accent}</span>
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          custom={0}
          variants={fade}
          initial="hidden"
          animate="visible"
          data-testid="hero-subtitle"
          className="mt-8 max-w-xl text-base sm:text-lg text-white/60 leading-relaxed"
        >
          {h.subtitle}
        </motion.p>

        <motion.div
          custom={1}
          variants={fade}
          initial="hidden"
          animate="visible"
          className="mt-12 flex flex-wrap items-center gap-4"
        >
          <a
            href="#apps"
            data-testid="hero-cta-explore"
            className="group flex items-center gap-2 rounded-full bg-blue-600 px-7 py-4 text-sm font-semibold text-white hover:bg-blue-500 transition-colors duration-300 shadow-[0_0_30px_rgba(37,99,235,0.35)]"
          >
            {h.ctaExplore}
            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
          </a>
          <a
            href="#manifesto"
            data-testid="hero-cta-manifesto"
            className="rounded-full border border-white/15 px-7 py-4 text-sm font-medium text-white/80 hover:text-white hover:border-white/40 transition-colors duration-300"
          >
            {h.ctaManifesto}
          </a>
        </motion.div>

        <motion.div
          custom={2}
          variants={fade}
          initial="hidden"
          animate="visible"
          className="mt-20 flex items-center gap-10 text-white/40"
          data-testid="hero-stats"
        >
          {h.stats.map(([n, l]) => (
            <div key={l} className="flex flex-col">
              <span className="font-display text-2xl font-semibold text-white">
                {n}
              </span>
              <span className="text-xs uppercase tracking-[0.2em] mt-1">{l}</span>
            </div>
          ))}
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-white/40"
      >
        <ArrowDown className="w-4 h-4 animate-bounce" />
        <span className="text-[10px] uppercase tracking-[0.3em]">{h.scroll}</span>
      </motion.div>
    </section>
  );
};
